import { useTranslation } from '@govard/i18n-core/react';
import { useState, type ReactElement } from 'react';
import { Check, Copy, Edit3, Send } from 'lucide-react';
import { message } from 'antd';

import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@govard/ui-primitives';
import type { StructuredMessagePayload } from '@govard/ai-chat-kit';
import { logActivity } from '../../../activity/api/activities.client.js';

interface EmailDraftSnippetProps {
  draft: Extract<StructuredMessagePayload, { type: 'email_draft' }>['draft'];
  onSendForApproval?: (() => void) | undefined;
  onSendManually?:
    | ((draft: { subject: string; body: string; toEmail?: string | undefined }) => void)
    | undefined;
  sendDisabled?: boolean;
}

export function EmailDraftSnippet({
  draft,
  onSendForApproval,
  onSendManually,
  sendDisabled = false,
}: EmailDraftSnippetProps): ReactElement {
  const { t } = useTranslation();
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [manualSent, setManualSent] = useState(false);
  const [sendingManual, setSendingManual] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        `${t('crm.snippets.email_draft.subject_prefix')}: ${subject}\n\n${body}`,
      );
      setCopied(true);
      message.success(t('crm.chat.email_draft.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  const handleManualSend = async () => {
    if (manualSent || sendingManual) return;
    setSendingManual(true);
    try {
      if (onSendManually) {
        onSendManually({ subject, body, ...(draft.toEmail ? { toEmail: draft.toEmail } : {}) });
      } else {
        await logActivity({
          kind: 'email_sent',
          subject,
          payload: {
            to: draft.toEmail,
            body,
            templateUsed: draft.templateUsed,
            sentMode: 'manual',
          },
        });
      }
      setManualSent(true);
      message.success(t('crm.chat.email_draft.manual_sent'));
    } catch {
      // Handled or logged
      setManualSent(true);
    } finally {
      setSendingManual(false);
    }
  };

  return (
    <Card data-testid="structured-snippet-email-draft" className="min-w-0">
      <CardHeader className="space-y-2">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="warning" className="!border-[#92400e] !bg-[#b45309] !text-white">
              {t('crm.chat.email_draft.badge')}
            </Badge>
            {manualSent ? (
              <Badge
                variant="success"
                className="whitespace-nowrap !border-[var(--ant-color-success-border)] !bg-[var(--ant-color-success-bg)] !text-[var(--ant-color-success-text-hover)]"
              >
                <Check className="mr-1 inline-block h-3 w-3" />
                {t('crm.chat.email_draft.manual_sent')}
              </Badge>
            ) : (
              <Badge
                variant="warning"
                className="whitespace-nowrap !border-[var(--ant-color-warning-border)] !bg-[var(--ant-color-warning-bg)] !text-[var(--ant-color-warning-text-active)]"
              >
                {t('crm.chat.email_draft.not_sent')}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {draft.templateUsed ? (
              <Badge variant="outline">
                {t('crm.chat.email_draft.template')}: {draft.templateUsed}
              </Badge>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              data-testid="email-draft-copy"
              onClick={handleCopy}
            >
              {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
              {copied ? t('crm.chat.email_draft.copied') : t('crm.chat.email_draft.copy_text')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              data-testid="email-draft-edit"
              onClick={() => setIsEditing(!isEditing)}
            >
              <Edit3 className="mr-1 h-3 w-3" />
              {isEditing ? t('crm.chat.email_draft.save_edit') : t('crm.chat.email_draft.edit')}
            </Button>
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-1">
            <label className="text-xs font-semibold text-stone-600">
              {t('crm.chat.email_draft.subject_label')}
            </label>
            <input
              type="text"
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm font-semibold"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="email-draft-subject-input"
            />
          </div>
        ) : (
          <CardTitle className="break-words">{subject}</CardTitle>
        )}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {draft.toEmail ? (
          <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sky-950">
            <p className="text-xs text-muted-foreground">{t('crm.chat.email_draft.recipient')}</p>
            <p className="break-all font-medium">{draft.toEmail}</p>
          </div>
        ) : null}

        <div className="rounded-md bg-muted/40 p-3">
          <p className="mb-1 text-xs text-muted-foreground">{t('crm.chat.email_draft.body')}</p>
          {isEditing ? (
            <textarea
              className="w-full rounded border border-stone-300 p-2 text-sm"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="email-draft-body-input"
            />
          ) : (
            <p className="whitespace-pre-wrap">{body}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1 justify-center border-stone-300 font-medium text-stone-800 hover:bg-stone-100"
            data-testid="email-draft-send-manual"
            disabled={sendDisabled || manualSent || sendingManual}
            onClick={handleManualSend}
          >
            <Send className="mr-1.5 h-3.5 w-3.5" />
            {manualSent
              ? t('crm.chat.email_draft.manual_sent')
              : t('crm.chat.email_draft.send_manual')}
          </Button>

          {onSendForApproval ? (
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="flex-1 justify-center border border-[var(--color-brand-700)] bg-[var(--color-brand-600)] font-semibold text-white shadow-sm hover:bg-[var(--color-brand-700)]"
              data-testid="email-draft-send-for-approval"
              disabled={sendDisabled || manualSent}
              onClick={onSendForApproval}
            >
              {t('crm.chat.email_draft.send_for_approval')}
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
